import { useState, useEffect, useRef, useCallback } from "react";
import {
    Search, RefreshCw, Filter, ChevronUp, ChevronDown,
    Eye, FileText, Download, X, Plus, Trash2, Truck
} from "lucide-react";
import * as XLSX from "xlsx";
import API_BASE_URL from "../config";

const SO_API = `${API_BASE_URL}api/SO_Marketing`;
const CUST_API = `${API_BASE_URL}api/Customer_marketing`;
const INV_API = `${API_BASE_URL}api/Inventory_Marketing`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "2-digit" }) : "—";

const fmtAmt = (n) =>
    n != null ? Number(n).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";

const getUser = () => {
    try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        return `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unknown";
    } catch { return "Unknown"; }
};

const StatusBadge = ({ status }) => {
    const map = {
        "Open": { bg: "#d1fae5", color: "#065f46" },
        "Open - Printed": { bg: "#dbeafe", color: "#1e40af" },
        "Closed": { bg: "#f1f5f9", color: "#475569" },
        "Cancelled": { bg: "#fee2e2", color: "#991b1b" },
        "Delivered": { bg: "#ede9fe", color: "#5b21b6" },
    };
    const s = map[status] || { bg: "#e0f2fe", color: "#0369a1" };
    return (
        <span style={{ padding: ".2rem .65rem", borderRadius: "6px", fontSize: ".78rem", fontWeight: 600, background: s.bg, color: s.color }}>
            {status || "—"}
        </span>
    );
};

// ─── DR GATEPASS MODAL ────────────────────────────────────────────────────────
const GatepassModal = ({ delivery, onClose }) => {
    const printRef = useRef();
    const [preparedBy, setPreparedBy] = useState("");
    const [verifiedBy, setVerifiedBy] = useState(delivery?.preparedBy || "");
    const [dispatchBy, setDispatchBy] = useState("");

    if (!delivery) return null;

    const now = new Date();
    const pDate = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${now.getFullYear()}`;
    const delDate = delivery.deliveryDate
        ? new Date(delivery.deliveryDate).toLocaleDateString("en-PH", { month: "2-digit", day: "2-digit", year: "numeric" }).replace(/\//g, "-")
        : "";

    const handlePrint = () => {
        const win = window.open("", "_blank");
        win.document.write(`
      <html>
        <head>
          <title>DR Gatepass - ${delivery.number}</title>
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background:#fff; padding:20px; }
            .company-header { text-align:center; margin-bottom:10px; }
            .company-name { font-size:13px; font-weight:bold; }
            .company-addr { font-size:10px; }
            .doc-title { font-size:12px; font-weight:bold; margin-top:4px; }
            table { width:100%; border-collapse:collapse; }
            th,td { padding:4px 8px; border:1px solid #999; font-size:10px; }
            th { background:#e8ecf5; font-weight:bold; text-align:left; }
            .label { color:#555; font-size:9px; font-weight:bold; }
            .field-row { display:flex; gap:8px; margin-bottom:4px; font-size:10px; }
            .field-label { color:#555; min-width:140px; }
            .sig-row { display:flex; gap:40px; margin-top:30px; }
            .sig-box { flex:1; text-align:center; }
.sig-line { border-top:1px solid #000; margin-top:40px; padding-top:4px; font-size:10px; }
.ack-section { margin-top:20px; border:1px solid #999; padding:10px; }
@page { size:A4; margin:15mm; }
          </style>
        </head>
        <body>${printRef.current.outerHTML}</body>
      </html>
    `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
    };

    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.6)", backdropFilter: "blur(6px)" }} onClick={onClose} />
            <div style={{
                position: "relative", background: "#fff", borderRadius: "18px",
                boxShadow: "0 20px 60px rgba(0,0,0,.25)", width: "90%", maxWidth: "780px",
                maxHeight: "94vh", display: "flex", flexDirection: "column", zIndex: 10, overflow: "hidden",
                fontFamily: '"Outfit",sans-serif'
            }}>
                {/* Header */}
                <div style={{ background: "linear-gradient(135deg,#1e3a8a,#2563eb)", padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                        <Truck size={20} color="#fff" />
                        <span style={{ color: "#fff", fontWeight: 700, fontSize: "1rem" }}>DR Gatepass</span>
                        <span style={{ background: "rgba(255,255,255,.2)", color: "#fff", fontSize: ".75rem", fontWeight: 600, padding: ".2rem .75rem", borderRadius: "20px" }}>
                            WB REF: {delivery.number}
                        </span>
                    </div>
                    <div style={{ display: "flex", gap: ".5rem" }}>
                        <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: ".4rem", background: "#fff", color: "#1e3a8a", border: "none", borderRadius: "8px", padding: ".5rem 1rem", fontWeight: 700, fontSize: ".82rem", cursor: "pointer" }}>
                            <Download size={15} /> Print / Export PDF
                        </button>
                        <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: "8px", padding: ".4rem", cursor: "pointer", color: "#fff", display: "flex" }}>
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Scrollable */}
                <div style={{ overflowY: "auto", flex: 1, background: "#f1f5f9", padding: "1.5rem" }}>
                    <div ref={printRef} style={{ background: "#fff", maxWidth: "680px", margin: "0 auto", padding: "20px 24px", boxShadow: "0 2px 16px rgba(0,0,0,.1)", borderRadius: "6px", fontFamily: "Arial,sans-serif", fontSize: "11px", color: "#000" }}>

                        {/* Company Header */}
                        <div style={{ textAlign: "center", marginBottom: "10px" }}>
                            <div style={{ fontSize: "13px", fontWeight: "bold" }}>MEGA SOFT HYGIENIC PRODUCT INC.</div>
                            <div style={{ fontSize: "10px" }}>38 - E Pleasant Drive, Pleasant View Subd., Libis, Caloocan City</div>
                            <div style={{ fontSize: "10px" }}>Telefax: 984-10-01</div>
                            <div style={{ fontSize: "12px", fontWeight: "bold", marginTop: "4px" }}>GATEPASS</div>
                        </div>

                        <hr style={{ borderColor: "#ccc", marginBottom: "10px" }} />

                        {/* Delivery To section */}
                        <div style={{ marginBottom: "8px" }}>
                            <div style={{ fontSize: "10px", fontWeight: "bold", marginBottom: "6px" }}>DELIVERY TO</div>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                                {/* LEFT — delivery fields */}
                                <div style={{ flex: 1 }}>
                                    {[
                                        { label: "CUSTOMER NAME", val: delivery.name || delivery.customer },
                                        { label: "CUSTOMER ADDRESS", val: delivery.shipToDefault || delivery.customerAddress || "" },
                                        { label: "DELIVERY INSTRUCTION", val: delivery.deliveryInstruction || "" },
                                        { label: "DELIVERY DATE", val: delDate },
                                        { label: "EXPECTED ARRIVAL DATE", val: "" },
                                        { label: "TRUCK PLATE NO", val: delivery.truckPlateNo || "" },
                                    ].map(({ label, val }) => (
                                        <div key={label} style={{ display: "flex", gap: "8px", marginBottom: "3px", fontSize: "10px" }}>
                                            <span style={{ color: "#555", minWidth: "160px", fontWeight: "bold", fontSize: "9px" }}>{label}</span>
                                            <span style={{ borderBottom: "1px solid #999", flex: 1 }}>{val}</span>
                                        </div>
                                    ))}
                                    <div style={{ display: "flex", gap: "8px", marginTop: "4px", fontSize: "10px" }}>
                                        <span style={{ color: "#555", minWidth: "160px", fontWeight: "bold", fontSize: "9px" }}>Request by:</span>
                                        <span style={{ borderBottom: "1px solid #999", flex: 1 }}>{delivery.requestor || ""}</span>
                                    </div>
                                </div>

                                {/* RIGHT — WBREF, no border box */}
                                <div style={{ textAlign: "right", fontSize: "10px", minWidth: "100px", paddingLeft: "16px", border: "none", background: "transparent" }}>                                    <div style={{ fontSize: "9px", color: "#555" }}>WBREF: WB-</div>
                                    <div style={{ fontWeight: "bold", fontSize: "11px" }}>{delivery.number}</div>
                                    <div style={{ fontSize: "9px", color: "#555", marginTop: "6px" }}>Tarkie REF:</div>
                                    <div style={{ marginTop: "2px", minWidth: "80px" }}></div>
                                </div>
                            </div>
                        </div>

                        {/* Items Table */}
                        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "8px", fontSize: "10px" }}>
                            <thead>
                                <tr style={{ background: "#d0d8e8" }}>
                                    <th style={{ padding: "4px 8px", border: "1px solid #ccc", textAlign: "left" }}>PARTICULARS</th>
                                    <th style={{ padding: "4px 8px", border: "1px solid #ccc", textAlign: "left" }}>ITEM DESCRIPTION</th>
                                    <th style={{ padding: "4px 8px", border: "1px solid #ccc", textAlign: "right" }}>QTY</th>
                                    <th style={{ padding: "4px 8px", border: "1px solid #ccc", textAlign: "right" }}>PRICE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(delivery.items || []).map((item, i) => (
                                    <tr key={i}>
                                        <td style={{ padding: "4px 8px", border: "1px solid #ddd", fontWeight: 600 }}>{item.itemNo || "—"}</td>
                                        <td style={{ padding: "4px 8px", border: "1px solid #ddd" }}>{item.itemDescription || "—"}</td>
                                        <td style={{ padding: "4px 8px", border: "1px solid #ddd", textAlign: "right" }}>{Number(item.quantity || 0).toLocaleString(undefined, { minimumFractionDigits: 3 })}</td>
                                        <td style={{ padding: "4px 8px", border: "1px solid #ddd", textAlign: "right" }}>{fmtAmt(item.unitPrice)}</td>
                                    </tr>
                                ))}
                                {Array.from({ length: Math.max(0, 5 - (delivery.items || []).length) }).map((_, i) => (
                                    <tr key={`e${i}`}>
                                        {[...Array(4)].map((_, j) => <td key={j} style={{ padding: "10px 8px", border: "1px solid #ddd" }}>&nbsp;</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Signature Row */}
                        <div style={{ display: "flex", gap: "20px", marginTop: "24px" }}>
                            {[
                                { label: "Prepared by:", val: preparedBy, setter: setPreparedBy },
                                { label: "Verified by:", val: verifiedBy, setter: setVerifiedBy },
                                { label: "Dispatch by:", val: dispatchBy, setter: setDispatchBy },
                            ].map(({ label, val, setter }) => (
                                <div key={label} style={{ flex: 1, textAlign: "center" }}>
                                    <div style={{ fontSize: "9px", color: "#555", marginBottom: "4px" }}>{label}</div>
                                    <input
                                        value={val}
                                        onChange={e => setter(e.target.value)}
                                        placeholder="Type name..."
                                        style={{ width: "100%", border: "none", borderBottom: "1px solid #000", textAlign: "center", fontSize: "10px", fontWeight: "bold", outline: "none", background: "transparent", paddingBottom: "2px", minHeight: "28px", fontFamily: "Arial,sans-serif" }}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Customer Acknowledgment */}
                        <div style={{ marginTop: "16px", border: "1px solid #ccc", padding: "8px" }}>
                            <div style={{ fontSize: "9px", fontWeight: "bold", marginBottom: "6px" }}>CUSTOMERS ACKNOWLEDGMENT:</div>
                            <div style={{ display: "flex", gap: "8px", marginBottom: "4px", fontSize: "10px" }}>
                                <span style={{ color: "#555", minWidth: "160px", fontSize: "9px" }}>Customer Representative</span>
                                <span style={{ borderBottom: "1px solid #999", flex: 1 }}></span>
                            </div>
                            <div style={{ display: "flex", gap: "8px", marginBottom: "4px", fontSize: "10px" }}>
                                <span style={{ color: "#555", minWidth: "160px", fontSize: "9px" }}>Status of Delivered Items</span>
                                <span style={{ borderBottom: "1px solid #999", flex: 1 }}></span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                                <div style={{ textAlign: "center", minWidth: "200px" }}>
                                    <div style={{ borderBottom: "1px solid #000", minHeight: "30px" }}></div>
                                    <div style={{ fontSize: "9px", color: "#555", marginTop: "2px" }}>Signature over Printed Name/Date</div>
                                </div>
                            </div>
                        </div>

                        <div style={{ fontSize: "9px", color: "#555", marginTop: "8px" }}>2-Customer Copy/1-Logistics Copy/1-File Copy</div>

                        {/* Stamp */}
                        <div style={{ marginTop: "24px", border: "2px solid #4472c4", display: "inline-block", minWidth: "320px" }}>
                            {/* Header */}
                            <div style={{ textAlign: "center", padding: "6px 10px 2px", borderBottom: "1px solid #4472c4" }}>
                                <div style={{ fontSize: "11px", fontWeight: "bold", color: "#4472c4" }}>MEGA SOFT HYGIENIC PRODUCTS INC.</div>
                                <div style={{ fontSize: "10px", fontWeight: "bold", color: "#4472c4", marginTop: "1px" }}>RELEASED</div>
                            </div>
                            {/* 4 Columns: DEPT | ACCOUNTING | APPROVER | SG */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", borderBottom: "1px solid #4472c4" }}>
                                {["DEPT:______", "ACCOUNTING", "APPROVER", "SG"].map((col, i) => (
                                    <div key={col} style={{
                                        textAlign: "center",
                                        padding: "2px 4px",
                                        borderRight: i < 3 ? "1px solid #4472c4" : "none",
                                        fontSize: "8px",
                                        fontWeight: "bold",
                                        color: "#4472c4"
                                    }}>
                                        {col}
                                    </div>
                                ))}
                            </div>
                            {/* Signature row */}
                            {/* Signature row */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", borderBottom: "1px solid #4472c4", minHeight: "40px" }}>
                                {[0, 1, 2, 3].map(i => (
                                    <div key={i} style={{
                                        borderRight: i < 3 ? "1px solid #4472c4" : "none",
                                        minHeight: "40px",
                                        padding: "4px 6px",
                                        display: "flex",
                                        alignItems: "flex-end"
                                    }}>
                                        <div style={{ borderBottom: "1px solid #4472c4", width: "100%", minHeight: "16px" }} />
                                    </div>
                                ))}
                            </div>
                            {/* DATE | TIME OUT row */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", padding: "3px 8px" }}>
                                {["DATE:", "TIME OUT:"].map(l => (
                                    <div key={l} style={{ fontSize: "8px", color: "#4472c4", fontWeight: "bold" }}>{l}</div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── DELIVERY FORM ────────────────────────────────────────────────────────────
const DeliveryForm = ({ initialData, onClose, onSaved }) => {
    const preparedBy = getUser();
    const today = new Date().toISOString().split("T")[0];

    const blankForm = {
        number: "", series: "Primary", status: "Open",
        customer: "", name: "", contactPerson: "", customerRefNo: "",
        localCurrency: "PHP", postingDate: today, deliveryDate: "",
        documentDate: today, salesEmployee: "No Sales Employee",
        preparedBy: getUser(),
        owner: "", remarks: "", requestor: "", department: "",
        billOfLading: "", discount: "", rounding: false, tax: "",
        shipToDefault: "", truckPlateNo: "", deliveryInstruction: "",
        items: [{ itemNo: "", itemDescription: "", quantity: "", uom: "", whse: "", addtlInfo: "", inStock: "", amount: "", totalLC: "", unitPrice: "", department: "", itemDesc2: "" }],
    };

    const [form, setForm] = useState(
        initialData
            ? { ...blankForm, ...initialData, preparedBy: getUser() } // ✅ override kahit may initialData
            : blankForm
    )
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [gatepassOpen, setGatepassOpen] = useState(false);
    const [custModalOpen, setCustModalOpen] = useState(false);
    const [invModalOpen, setInvModalOpen] = useState(false);
    const [activeRowIdx, setActiveRowIdx] = useState(null);
    const [shipToDefault, setShipToDefault] = useState(initialData?.shipToDefault || "");
    const [activeTab, setActiveTab] = useState("contents");

    // Open gatepass immediately if flagged (e.g. from "Gatepass" button in list)
    useEffect(() => {
        if (initialData?._openGatepass) {
            setGatepassOpen(true);
        }
    }, []);

    // Fetch shipToDefault when customer changes
    useEffect(() => {
        if (!form.customer) return;
        fetch(CUST_API)
            .then(r => r.json())
            .then(data => {
                const match = data.find(c => c.bpCode?.toLowerCase() === form.customer?.toLowerCase());
                if (match?.shipToDefault) {
                    setShipToDefault(match.shipToDefault);
                    setForm(f => ({ ...f, shipToDefault: match.shipToDefault }));
                }
            }).catch(() => { });
    }, [form.customer]);

    const handleField = (field, val) => setForm(f => ({ ...f, [field]: val }));

    const handleItem = (idx, field, value) => {
        const items = [...form.items];
        items[idx] = { ...items[idx], [field]: value };
        if (field === "unitPrice" || field === "quantity") {
            const qty = Number(field === "quantity" ? value : items[idx].quantity) || 0;
            const price = Number(field === "unitPrice" ? value : items[idx].unitPrice) || 0;
            items[idx].amount = (qty * price).toFixed(4);
            items[idx].totalLC = items[idx].amount;
        }
        setForm(f => ({ ...f, items }));
    };

    const totalBefore = form.items.reduce((s, i) => s + (Number(i.unitPrice) || 0) * (Number(i.quantity) || 0), 0);
    const total = totalBefore - (Number(form.discount) || 0) + (Number(form.tax) || 0);

    const handlePost = async () => {
        if (!form.deliveryDate) {
            setError("Delivery Date is required before posting."); return;
        }
        setLoading(true); setError(null);
        try {
            const payload = {
                ...form,
                status: "Open - Printed",
                preparedBy: form.preparedBy,
                totalBeforeDiscount: totalBefore,
                total,
                discount: Number(form.discount) || 0,
                tax: Number(form.tax) || 0,
                items: form.items.map(i => ({
                    ...i,
                    quantity: Number(i.quantity) || 0,
                    unitPrice: Number(i.unitPrice) || 0,
                    amount: Number(i.amount) || 0,
                    totalLC: Number(i.totalLC) || 0,
                    inStock: Number(i.inStock) || 0,
                })),
            };

            const method = form.id ? "PUT" : "POST";
            const url = form.id ? `${SO_API}/${form.id}` : SO_API;
            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", Accept: "*/*" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`Server error ${res.status}`);
            const saved = await res.json().catch(() => payload);

            // ── Deduct available qty from Marketing_items ──────────────────
            const itemsToDeduct = form.items
                .filter(i => i.itemNo && Number(i.quantity) > 0)
                .map(i => ({
                    itemCode: i.itemNo,   // itemNo field holds the itemCode
                    quantity: Number(i.quantity),
                }));

            if (itemsToDeduct.length > 0) {
                const deductRes = await fetch(
                    `${API_BASE_URL}api/Marketing_items/deduct-delivery`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Accept: "*/*" },
                        body: JSON.stringify(itemsToDeduct),
                    }
                );
                if (!deductRes.ok) {
                    const deductErr = await deductRes.json().catch(() => ({}));
                    // Warn lang, hindi block ang gatepass — delivery na-save na
                    console.warn("Stock deduction warning:", deductErr);
                    setError(`Delivery posted but stock deduction failed: ${deductErr.message || "Unknown error"}`);
                }
            }
            // ──────────────────────────────────────────────────────────────

            setForm(f => ({ ...f, ...saved, status: "Open - Printed" }));
            onSaved && onSaved();
            setGatepassOpen(true);
        } catch (err) { setError(err.message); }
        setLoading(false);
    };
    // Customer modal
    const [customers, setCustomers] = useState([]);
    const [custSearch, setCustSearch] = useState("");
    useEffect(() => {
        if (!custModalOpen) return;
        fetch(CUST_API).then(r => r.json()).then(setCustomers).catch(() => { });
    }, [custModalOpen]);
    const filteredCust = customers.filter(c =>
        c.bpCode?.toLowerCase().includes(custSearch.toLowerCase()) ||
        c.bpName?.toLowerCase().includes(custSearch.toLowerCase())
    );

    // Inventory modal
    const [inventory, setInventory] = useState([]);
    const [invSearch, setInvSearch] = useState("");
    useEffect(() => {
        if (!invModalOpen) return;
        fetch(INV_API).then(r => r.json()).then(setInventory).catch(() => { });
    }, [invModalOpen]);
    const filteredInv = inventory.filter(i =>
        i.itemNo?.toLowerCase().includes(invSearch.toLowerCase()) ||
        i.itemDescription?.toLowerCase().includes(invSearch.toLowerCase())
    );

    return (
        <>
            {gatepassOpen && <GatepassModal delivery={form} onClose={() => setGatepassOpen(false)} />}

            {/* Customer Modal */}
            {custModalOpen && (
                <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.5)", backdropFilter: "blur(4px)" }} onClick={() => setCustModalOpen(false)} />
                    <div style={{ position: "relative", background: "#fff", borderRadius: "16px", width: "90%", maxWidth: "640px", maxHeight: "80vh", display: "flex", flexDirection: "column", zIndex: 10, overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,.2)" }}>
                        <div style={{ background: "#1d4ed8", padding: ".875rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Customer Lookup</span>
                            <button onClick={() => setCustModalOpen(false)} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: "6px", padding: ".3rem", cursor: "pointer", color: "#fff", display: "flex" }}><X size={16} /></button>
                        </div>
                        <div style={{ padding: ".75rem 1rem", borderBottom: "1px solid #e2e8f0" }}>
                            <input autoFocus value={custSearch} onChange={e => setCustSearch(e.target.value)} placeholder="Search BP code or name…" style={{ width: "100%", padding: ".625rem 1rem", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: ".875rem", outline: "none" }} />
                        </div>
                        <div style={{ overflowY: "auto", flex: 1 }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
                                <thead><tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e2e8f0" }}>
                                    {["BP Code", "BP Name", "Group", "Sales Employee"].map(h => <th key={h} style={{ padding: ".625rem 1rem", textAlign: "left", fontWeight: 700, color: "#475569", fontSize: ".75rem", textTransform: "uppercase" }}>{h}</th>)}
                                </tr></thead>
                                <tbody>
                                    {filteredCust.map(c => (
                                        <tr key={c.id} onClick={() => { setForm(f => ({ ...f, customer: c.bpCode || "", name: c.bpName || "" })); setCustModalOpen(false); }} style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                                            <td style={{ padding: ".625rem 1rem", fontWeight: 600, color: "#3b82f6", fontFamily: "monospace", fontSize: ".82rem" }}>{c.bpCode}</td>
                                            <td style={{ padding: ".625rem 1rem", color: "#334155" }}>{c.bpName || "—"}</td>
                                            <td style={{ padding: ".625rem 1rem", color: "#64748b" }}>{c.groupCode || "—"}</td>
                                            <td style={{ padding: ".625rem 1rem", color: "#64748b" }}>{c.salesEmployeeCode || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Inventory Modal */}
            {invModalOpen && (
                <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.5)", backdropFilter: "blur(4px)" }} onClick={() => setInvModalOpen(false)} />
                    <div style={{ position: "relative", background: "#fff", borderRadius: "16px", width: "90%", maxWidth: "760px", maxHeight: "80vh", display: "flex", flexDirection: "column", zIndex: 10, overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,.2)" }}>
                        <div style={{ background: "#1d4ed8", padding: ".875rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Inventory Lookup</span>
                            <button onClick={() => setInvModalOpen(false)} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: "6px", padding: ".3rem", cursor: "pointer", color: "#fff", display: "flex" }}><X size={16} /></button>
                        </div>
                        <div style={{ padding: ".75rem 1rem", borderBottom: "1px solid #e2e8f0" }}>
                            <input autoFocus value={invSearch} onChange={e => setInvSearch(e.target.value)} placeholder="Search item no or description…" style={{ width: "100%", padding: ".625rem 1rem", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: ".875rem", outline: "none" }} />
                        </div>
                        <div style={{ overflowY: "auto", flex: 1 }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
                                <thead><tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e2e8f0" }}>
                                    {["Item No.", "Description", "Whse", "Available", "Status"].map(h => <th key={h} style={{ padding: ".625rem 1rem", textAlign: "left", fontWeight: 700, color: "#475569", fontSize: ".75rem", textTransform: "uppercase" }}>{h}</th>)}
                                </tr></thead>
                                <tbody>
                                    {filteredInv.map(item => (
                                        <tr key={item.id} onClick={() => {
                                            const items = [...form.items];
                                            items[activeRowIdx] = { ...items[activeRowIdx], itemNo: item.itemNo || "", itemDescription: item.itemDescription || "", whse: item.whse || "", inStock: item.availableQty ?? item.quantity ?? "" };
                                            setForm(f => ({ ...f, items }));
                                            setInvModalOpen(false); setActiveRowIdx(null);
                                        }} style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                                            <td style={{ padding: ".625rem 1rem", fontWeight: 600, color: "#3b82f6", fontFamily: "monospace", fontSize: ".82rem" }}>{item.itemNo}</td>
                                            <td style={{ padding: ".625rem 1rem", color: "#334155" }}>{item.itemDescription || "—"}</td>
                                            <td style={{ padding: ".625rem 1rem" }}><span style={{ background: "#f1f5f9", color: "#475569", padding: ".2rem .5rem", borderRadius: "4px", fontSize: ".78rem", fontFamily: "monospace" }}>{item.whse || "—"}</span></td>
                                            <td style={{ padding: ".625rem 1rem", fontWeight: 700, color: Number(item.availableQty) > 0 ? "#059669" : "#ef4444" }}>{Number(item.availableQty || 0).toLocaleString()}</td>
                                            <td style={{ padding: ".625rem 1rem" }}>
                                                {Number(item.availableQty) > 9 ? <span style={{ background: "#d1fae5", color: "#065f46", padding: ".2rem .5rem", borderRadius: "4px", fontSize: ".75rem", fontWeight: 600 }}>In Stock</span>
                                                    : Number(item.availableQty) > 0 ? <span style={{ background: "#fef3c7", color: "#92400e", padding: ".2rem .5rem", borderRadius: "4px", fontSize: ".75rem", fontWeight: 600 }}>Low</span>
                                                        : <span style={{ background: "#fee2e2", color: "#991b1b", padding: ".2rem .5rem", borderRadius: "4px", fontSize: ".75rem", fontWeight: 600 }}>Out</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Form */}
            <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", background: "rgba(15,23,42,.5)", backdropFilter: "blur(6px)", padding: "1rem" }}>
                <div style={{ background: "#fff", borderRadius: "18px", width: "100%", maxWidth: "1200px", boxShadow: "0 20px 60px rgba(0,0,0,.2)", fontFamily: '"Outfit",sans-serif', margin: "auto" }}>

                    {/* Title */}
                    <div style={{ background: "linear-gradient(135deg,#1e3a8a,#2563eb)", padding: "1rem 1.5rem", borderRadius: "18px 18px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                            <Truck size={20} color="#fff" />
                            <span style={{ color: "#fff", fontWeight: 700, fontSize: "1rem" }}>Delivery Form</span>
                            {form.number && <span style={{ background: "rgba(255,255,255,.2)", color: "#fff", fontSize: ".75rem", fontWeight: 600, padding: ".2rem .75rem", borderRadius: "20px" }}>#{form.number}</span>}
                        </div>
                        <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: "8px", padding: ".4rem", cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
                    </div>

                    {error && (
                        <div style={{ margin: "1rem 1.5rem 0", background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b", padding: ".75rem 1rem", borderRadius: "8px", fontSize: ".875rem", display: "flex", alignItems: "center", gap: ".5rem" }}>
                            <span>{error}</span>
                            <button onClick={() => setError(null)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#991b1b" }}>✕</button>
                        </div>
                    )}

                    <div style={{ padding: "1.25rem 1.5rem" }}>
                        {/* Top section: 3 column grid */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1.25rem", marginBottom: "1rem" }}>

                            {/* Left: Customer */}
                            <div style={{ display: "flex", flexDirection: "column", gap: ".625rem" }}>
                                {[
                                    { label: "Customer", field: "customer", searchBtn: true },
                                    { label: "Name", field: "name" },
                                    { label: "Contact Person", field: "contactPerson" },
                                    { label: "Customer Ref. No.", field: "customerRefNo" },
                                    { label: "Local Currency", field: "localCurrency", type: "select", options: ["PHP", "USD"] },
                                ].map(({ label, field, searchBtn, type, options }) => (
                                    <div key={field} style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                                        <label style={{ fontSize: ".72rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em", minWidth: "110px" }}>{label}</label>
                                        {type === "select" ? (
                                            <select value={form[field]} onChange={e => handleField(field, e.target.value)} style={{ flex: 1, padding: ".5rem .75rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: ".875rem", outline: "none" }}>
                                                {options.map(o => <option key={o}>{o}</option>)}
                                            </select>
                                        ) : (
                                            <div style={{ position: "relative", flex: 1 }}>
                                                <input value={form[field]} onChange={e => handleField(field, e.target.value)} style={{ width: "100%", padding: ".5rem .75rem", paddingRight: searchBtn ? "2rem" : ".75rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: ".875rem", outline: "none", background: "#fefce8" }} />
                                                {searchBtn && (
                                                    <button onClick={() => setCustModalOpen(true)} style={{ position: "absolute", right: ".5rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex" }}>
                                                        <Search size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Middle: Authorization */}
                            <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: "12px", padding: "1rem" }}>
                                <p style={{ fontSize: ".72rem", fontWeight: 700, color: "#0369a1", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: ".875rem" }}>Authorization</p>
                                {[
                                    { label: "Prepared By", field: "preparedBy", readOnly: true },
                                    { label: "Checked By", field: "checkedBy" },
                                    { label: "Approved By", field: "approvedBy" },
                                    { label: "Requestor", field: "requestor" },
                                    { label: "Department", field: "department" },
                                ].map(({ label, field, readOnly }) => (
                                    <div key={field} style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".5rem" }}>
                                        <label style={{ fontSize: ".72rem", fontWeight: 600, color: "#0369a1", textTransform: "uppercase", letterSpacing: ".04em", minWidth: "100px" }}>{label}</label>
                                        <input value={form[field] || ""} onChange={e => !readOnly && handleField(field, e.target.value)} readOnly={readOnly}
                                            style={{ flex: 1, padding: ".5rem .75rem", border: "1px solid #bae6fd", borderRadius: "6px", fontSize: ".875rem", outline: "none", background: readOnly ? "#e0f2fe" : "#fff", color: readOnly ? "#0369a1" : "#1e293b", fontWeight: readOnly ? 600 : 400 }} />
                                    </div>
                                ))}
                            </div>

                            {/* Right: Order Info */}
                            <div style={{ background: "#f8faff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "1rem" }}>
                                <p style={{ fontSize: ".72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: ".875rem" }}>Order Info</p>
                                {[
                                    { label: "No.", field: "number" },
                                    { label: "Status", field: "status", type: "select", options: ["Open", "Open - Printed", "Closed", "Cancelled", "Delivered"] },
                                    { label: "Posting Date", field: "postingDate", type: "date" },
                                    { label: "Delivery Date", field: "deliveryDate", type: "date", highlight: true },
                                    { label: "Document Date", field: "documentDate", type: "date" },
                                ].map(({ label, field, type, options, highlight }) => (
                                    <div key={field} style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".5rem" }}>
                                        <label style={{ fontSize: ".72rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em", minWidth: "100px" }}>{label}</label>
                                        {type === "select" ? (
                                            <select value={form[field]} onChange={e => handleField(field, e.target.value)} style={{ flex: 1, padding: ".5rem .75rem", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: ".875rem", outline: "none" }}>
                                                {options.map(o => <option key={o}>{o}</option>)}
                                            </select>
                                        ) : type === "date" ? (
                                            <input type="date" value={form[field] || ""} onChange={e => handleField(field, e.target.value)}
                                                style={{ flex: 1, padding: ".5rem .75rem", border: `1px solid ${highlight ? "#f59e0b" : "#e2e8f0"}`, borderRadius: "6px", fontSize: ".875rem", outline: "none", background: highlight ? "#fffbeb" : "#fff" }} />
                                        ) : (
                                            <input value={form[field] || ""} onChange={e => handleField(field, e.target.value)}
                                                style={{ flex: 1, padding: ".5rem .75rem", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: ".875rem", outline: "none" }} />
                                        )}
                                    </div>
                                ))}
                                {/* Ship To */}
                                <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".5rem" }}>
                                    <label style={{ fontSize: ".72rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em", minWidth: "100px" }}>Ship To</label>
                                    <input value={form.shipToDefault || ""} onChange={e => handleField("shipToDefault", e.target.value)}
                                        style={{ flex: 1, padding: ".5rem .75rem", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: ".875rem", outline: "none" }} />
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div style={{ borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0", display: "flex", marginBottom: 0 }}>
                            {["contents", "logistics", "accounting", "attachments"].map(tab => (
                                <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: ".75rem 1.25rem", border: "none", borderBottom: `2px solid ${activeTab === tab ? "#1d4ed8" : "transparent"}`, background: activeTab === tab ? "#eff6ff" : "transparent", color: activeTab === tab ? "#1d4ed8" : "#64748b", fontWeight: 600, fontSize: ".85rem", cursor: "pointer", textTransform: "capitalize", fontFamily: '"Outfit",sans-serif' }}>
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Contents Tab */}
                        {activeTab === "contents" && (
                            <div style={{ overflowX: "auto", marginTop: 0 }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".83rem" }}>
                                    <thead>
                                        <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e2e8f0" }}>
                                            {["#", "Item No.", "Description", "Qty", "UOM", "Whse", "Addtl Info", "In Stock", "Amount", "Total LC", "Unit Price", "Dept", "Item Desc2", ""].map(h => (
                                                <th key={h} style={{ padding: ".625rem .75rem", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: ".75rem", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.items.map((item, idx) => (
                                            <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                                <td style={{ padding: ".5rem .75rem", color: "#94a3b8", fontSize: ".8rem" }}>{idx + 1}</td>
                                                {/* Item No */}
                                                <td style={{ padding: ".375rem .5rem", minWidth: "120px" }}>
                                                    <div style={{ position: "relative" }}>
                                                        <input value={item.itemNo} onChange={e => handleItem(idx, "itemNo", e.target.value)} style={{ width: "100%", padding: ".4rem 1.75rem .4rem .5rem", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: ".83rem", outline: "none" }} />
                                                        <button onClick={() => { setActiveRowIdx(idx); setInvModalOpen(true); }} style={{ position: "absolute", right: ".3rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><Search size={12} /></button>
                                                    </div>
                                                </td>
                                                {/* Description */}
                                                <td style={{ padding: ".375rem .5rem", minWidth: "160px" }}>
                                                    <div style={{ position: "relative" }}>
                                                        <input value={item.itemDescription} onChange={e => handleItem(idx, "itemDescription", e.target.value)} style={{ width: "100%", padding: ".4rem 1.75rem .4rem .5rem", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: ".83rem", outline: "none" }} />
                                                        <button onClick={() => { setActiveRowIdx(idx); setInvModalOpen(true); }} style={{ position: "absolute", right: ".3rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><Search size={12} /></button>
                                                    </div>
                                                </td>
                                                {[
                                                    { f: "quantity", w: "70px", type: "number" },
                                                    { f: "uom", w: "70px" },
                                                    { f: "whse", w: "80px" },
                                                    { f: "addtlInfo", w: "100px" },
                                                    { f: "inStock", w: "80px", type: "number" },
                                                    { f: "amount", w: "90px", ro: true },
                                                    { f: "totalLC", w: "90px", ro: true },
                                                    { f: "unitPrice", w: "90px", type: "number" },
                                                    { f: "department", w: "90px" },
                                                    { f: "itemDesc2", w: "110px" },
                                                ].map(({ f, w, type, ro }) => (
                                                    <td key={f} style={{ padding: ".375rem .5rem", minWidth: w }}>
                                                        <input type={type || "text"} value={item[f] || ""} onChange={e => !ro && handleItem(idx, f, e.target.value)} readOnly={ro}
                                                            style={{ width: "100%", padding: ".4rem .5rem", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: ".83rem", outline: "none", background: ro ? "#f8f9fa" : "#fff", color: ro ? "#94a3b8" : "#1e293b" }} />
                                                    </td>
                                                ))}
                                                <td style={{ padding: ".375rem .5rem" }}>
                                                    {form.items.length > 1 && (
                                                        <button onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1" }} onMouseEnter={e => e.currentTarget.style.color = "#ef4444"} onMouseLeave={e => e.currentTarget.style.color = "#cbd5e1"}>
                                                            <Trash2 size={15} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Empty filler rows */}
                                        {Array.from({ length: Math.max(0, 4 - form.items.length) }).map((_, i) => (
                                            <tr key={`ef${i}`} style={{ borderBottom: "1px solid #f8f9fa" }}>
                                                {Array.from({ length: 14 }).map((_, j) => <td key={j} style={{ padding: "1.25rem .75rem" }}></td>)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div style={{ padding: ".75rem 1rem", borderTop: "1px solid #f1f5f9" }}>
                                    <button onClick={() => setForm(f => ({ ...f, items: [...f.items, { itemNo: "", itemDescription: "", quantity: "", uom: "", whse: "", addtlInfo: "", inStock: "", amount: "", totalLC: "", unitPrice: "", department: "", itemDesc2: "" }] }))} style={{ display: "flex", alignItems: "center", gap: ".4rem", background: "none", border: "none", cursor: "pointer", color: "#1d4ed8", fontWeight: 600, fontSize: ".875rem" }}>
                                        <Plus size={15} /> Add Row
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === "logistics" && (
                            <div style={{ padding: "1.25rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                                {[
                                    { label: "Bill of Lading", field: "billOfLading" },
                                    { label: "Truck Plate No.", field: "truckPlateNo" },
                                    { label: "Delivery Instruction", field: "deliveryInstruction" },
                                ].map(({ label, field }) => (
                                    <div key={field}>
                                        <label style={{ display: "block", fontSize: ".8rem", fontWeight: 600, color: "#475569", marginBottom: ".375rem", textTransform: "uppercase" }}>{label}</label>
                                        <input value={form[field] || ""} onChange={e => handleField(field, e.target.value)} style={{ width: "100%", padding: ".625rem .875rem", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: ".875rem", outline: "none" }} />
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === "accounting" && (
                            <div style={{ padding: "2rem", textAlign: "center", color: "#94a3b8" }}>Accounting fields — coming soon.</div>
                        )}
                        {activeTab === "attachments" && (
                            <div style={{ padding: "2rem", textAlign: "center", color: "#94a3b8" }}>No attachments yet.</div>
                        )}

                        {/* Bottom: totals + remarks */}
                        <div style={{ borderTop: "1px solid #e2e8f0", padding: "1rem 0 0", display: "flex", gap: "2rem" }}>
                            {/* Left */}
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: ".625rem" }}>
                                {[
                                    { label: "Sales Employee", field: "salesEmployee" },
                                    { label: "Owner", field: "owner" },
                                ].map(({ label, field }) => (
                                    <div key={field} style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                                        <label style={{ fontSize: ".75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", minWidth: "120px" }}>{label}</label>
                                        <input value={form[field] || ""} onChange={e => handleField(field, e.target.value)} style={{ flex: 1, maxWidth: "280px", padding: ".5rem .75rem", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: ".875rem", outline: "none" }} />
                                    </div>
                                ))}
                                <div style={{ display: "flex", alignItems: "flex-start", gap: ".75rem" }}>
                                    <label style={{ fontSize: ".75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", minWidth: "120px", marginTop: ".5rem" }}>Remarks</label>
                                    <textarea value={form.remarks || ""} onChange={e => handleField("remarks", e.target.value)} rows={3} style={{ flex: 1, maxWidth: "280px", padding: ".625rem .75rem", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: ".875rem", outline: "none", resize: "none" }} />
                                </div>
                            </div>

                            {/* Right: Totals */}
                            <div style={{ minWidth: "300px" }}>
                                {[
                                    { label: "Total Before Discount", val: totalBefore.toFixed(2), ro: true },
                                    { label: "Discount", field: "discount" },
                                    { label: "Tax", field: "tax" },
                                ].map(({ label, val, field, ro }) => (
                                    <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", marginBottom: ".5rem" }}>
                                        <span style={{ fontSize: ".78rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>{label}</span>
                                        {ro ? (
                                            <input value={val} readOnly style={{ width: "140px", padding: ".5rem .75rem", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: ".875rem", background: "#f8f9fa", textAlign: "right", color: "#64748b" }} />
                                        ) : (
                                            <input type="number" value={form[field] || ""} onChange={e => handleField(field, e.target.value)} style={{ width: "140px", padding: ".5rem .75rem", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: ".875rem", textAlign: "right", outline: "none" }} />
                                        )}
                                    </div>
                                ))}
                                <div style={{ borderTop: "2px solid #e2e8f0", paddingTop: ".5rem", display: "flex", justifyContent: "space-between" }}>
                                    <span style={{ fontWeight: 700, color: "#1e293b" }}>Total</span>
                                    <span style={{ fontWeight: 700, color: "#1d4ed8", fontSize: "1rem" }}>PHP {fmtAmt(total)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ borderTop: "1px solid #e2e8f0", padding: "1rem 1.5rem", display: "flex", alignItems: "center", gap: ".75rem" }}>
                        <button onClick={handlePost} disabled={loading} style={{ display: "flex", alignItems: "center", gap: ".5rem", background: loading ? "#94a3b8" : "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", border: "none", borderRadius: "8px", padding: ".625rem 1.5rem", fontWeight: 700, fontSize: ".875rem", cursor: loading ? "not-allowed" : "pointer", fontFamily: '"Outfit",sans-serif' }}>
                            <Truck size={16} /> {loading ? "Posting..." : "Post Delivery"}
                        </button>
                        {form.deliveryDate && (
                            <button onClick={() => setGatepassOpen(true)} style={{ display: "flex", alignItems: "center", gap: ".5rem", background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", border: "none", borderRadius: "8px", padding: ".625rem 1.5rem", fontWeight: 700, fontSize: ".875rem", cursor: "pointer", fontFamily: '"Outfit",sans-serif' }}>
                                <FileText size={16} /> Generate DR Gatepass
                            </button>
                        )}
                        <button onClick={onClose} style={{ padding: ".625rem 1.25rem", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", fontWeight: 600, fontSize: ".875rem", cursor: "pointer", color: "#475569", fontFamily: '"Outfit",sans-serif' }}>
                            Cancel
                        </button>
                    </div>

                </div>
            </div>
        </>
    );
};

// ─── PICK LIST MODAL ─────────────────────────────────────────────────────────
const PickListModal = ({ so, onClose }) => {
    const printRef = useRef();
    const [shipTo, setShipTo] = useState("");

    useEffect(() => {
        const fetchShipTo = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}api/Customer_marketing`);
                if (res.ok) {
                    const customers = await res.json();
                    const match = customers.find(
                        c => c.bpCode?.toLowerCase() === so.customer?.toLowerCase()
                    );
                    if (match?.shipToDefault) setShipTo(match.shipToDefault);
                }
            } catch (err) { console.error(err); }
        };
        if (so?.customer) fetchShipTo();
    }, [so?.customer]);

    if (!so) return null;

    const now = new Date();
    const pDate = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${now.getFullYear()}`;
    const pTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const docDate = so.documentDate ? new Date(so.documentDate).toLocaleDateString("en-PH", { month: "2-digit", day: "2-digit", year: "numeric" }).replace(/\//g, "-") : "—";
    const delDate = so.deliveryDate ? new Date(so.deliveryDate).toLocaleDateString("en-PH", { month: "2-digit", day: "2-digit", year: "numeric" }).replace(/\//g, "-") : "—";

    const subtotal = (so.items || []).reduce(
        (s, i) => s + (Number(i.unitPrice) || 0) * (Number(i.quantity) || 0), 0
    );
    const tax = Number(so.tax) || 0;
    const total = subtotal + tax - (Number(so.discount) || 0);

    const handlePrint = () => {
        const win = window.open("", "_blank");
        win.document.write(`
      <html>
        <head>
          <title>Pick List - SO #${so.number}</title>
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background:#fff; padding:16px; }
            table { width:100%; border-collapse:collapse; }
            th, td { padding: 4px 8px; border: 1px solid #ccc; font-size:10px; }
            th { background:#e8ecf5; font-weight:bold; text-align:left; }
            .blue-header { background:#4472c4; color:#fff; font-weight:bold; padding:4px 8px; font-size:10px; }
            @page { size:A4; margin:12mm; }
          </style>
        </head>
        <body>${printRef.current.outerHTML}</body>
      </html>
    `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
    };

    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.6)", backdropFilter: "blur(6px)" }} onClick={onClose} />
            <div style={{
                position: "relative", background: "#fff", borderRadius: "18px",
                boxShadow: "0 20px 60px rgba(0,0,0,.25)", width: "90%", maxWidth: "800px",
                maxHeight: "94vh", display: "flex", flexDirection: "column", zIndex: 10,
                fontFamily: '"Outfit", sans-serif', overflow: "hidden"
            }}>
                {/* Modal Header */}
                <div style={{ background: "linear-gradient(135deg,#1e3a8a,#2563eb)", padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                        <FileText size={20} color="#fff" />
                        <span style={{ color: "#fff", fontWeight: 700, fontSize: "1rem" }}>Pick List</span>
                        <span style={{ background: "rgba(255,255,255,.2)", color: "#fff", fontSize: ".75rem", fontWeight: 600, padding: ".2rem .75rem", borderRadius: "20px" }}>
                            SO #{so.number}
                        </span>
                    </div>
                    <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                        <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: ".4rem", background: "#fff", color: "#1e3a8a", border: "none", borderRadius: "8px", padding: ".5rem 1rem", fontWeight: 700, fontSize: ".82rem", cursor: "pointer", fontFamily: '"Outfit", sans-serif' }}>
                            <Download size={15} /> Export PDF
                        </button>
                        <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: "8px", padding: ".4rem", cursor: "pointer", color: "#fff", display: "flex" }}>
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Scrollable content */}
                <div style={{ overflowY: "auto", flex: 1, background: "#f1f5f9", padding: "1.5rem" }}>
                    <div ref={printRef} style={{ background: "#fff", maxWidth: "700px", margin: "0 auto", padding: "18px 22px", boxShadow: "0 2px 16px rgba(0,0,0,.12)", borderRadius: "6px", fontFamily: "Arial, sans-serif", fontSize: "11px", color: "#000" }}>

                        {/* Top row: address + date/time + title */}
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                            <div>
                                <div style={{ fontSize: "10px" }}>34 Pleasant View Subd</div>
                                <div style={{ fontSize: "10px" }}>Bagbaguin Caloocan City</div>
                                <div style={{ marginTop: "6px", fontSize: "10px" }}>PHILIPPINES</div>
                                <div style={{ fontSize: "10px" }}>PH <span style={{ marginLeft: "20px" }}>Internet:</span></div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: "9px", color: "#555", marginBottom: "2px" }}>{pDate} &nbsp; {pTime}</div>
                                <div style={{ fontSize: "9px", color: "#555", marginBottom: "1px" }}>Tel.:</div>
                                <div style={{ fontSize: "9px", color: "#555", marginBottom: "12px" }}>Fax:</div>
                                <div style={{ fontSize: "20px", fontWeight: "bold" }}>Pick List</div>
                            </div>
                        </div>

                        <hr style={{ borderColor: "#ccc", borderWidth: "1px 0 0 0", marginBottom: "8px" }} />

                        {/* Bill-To + Information side by side */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", marginBottom: "0" }}>
                            <div style={{ border: "1px solid #ccc", borderRight: "none" }}>
                                <div style={{ background: "#4472c4", color: "#fff", fontWeight: "bold", padding: "3px 8px", fontSize: "10px" }}>Bill-To Party</div>
                                <div style={{ padding: "6px 8px", minHeight: "70px" }}>
                                    <div style={{ fontWeight: 600, fontSize: "11px" }}>{so.name || so.customer || "—"}</div>
                                    <div style={{ fontSize: "10px", marginTop: "2px", color: "#444" }}>PHILIPPINES</div>
                                </div>
                            </div>
                            <div style={{ border: "1px solid #ccc" }}>
                                <div style={{ background: "#4472c4", color: "#fff", fontWeight: "bold", padding: "3px 8px", fontSize: "10px" }}>Information</div>
                                <div style={{ padding: "4px 8px" }}>
                                    {[
                                        { label: "Sales Order", val: so.number },
                                        { label: "Document Date", val: docDate },
                                        { label: "Customer Code", val: so.customer },
                                    ].map(({ label, val }) => (
                                        <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", padding: "2px 0" }}>
                                            <span style={{ color: "#555" }}>{label}</span>
                                            <span style={{ fontWeight: 600 }}>{val || "—"}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Ship-To Party */}
                        <div style={{ border: "1px solid #ccc", borderTop: "none", marginBottom: "8px" }}>
                            <div style={{ background: "#4472c4", color: "#fff", fontWeight: "bold", padding: "3px 8px", fontSize: "10px" }}>Ship-To Party</div>
                            <div style={{ padding: "6px 8px", minHeight: "48px" }}>
                                <div style={{ fontWeight: 600, fontSize: "11px" }}>{shipTo || so.name || so.customer || "—"}</div>
                                <div style={{ fontSize: "10px", color: "#444" }}>PHILIPPINES</div>
                            </div>
                        </div>

                        {/* Payment / Delivery row */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", marginBottom: "8px", border: "1px solid #ccc" }}>
                            <div style={{ padding: "4px 8px", borderRight: "1px solid #ccc" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", marginBottom: "3px" }}>
                                    <span style={{ color: "#555" }}>Payment Terms:</span>
                                    <span style={{ fontWeight: 600 }}>7 Days</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                                    <span style={{ color: "#555" }}>Gross Weight</span>
                                    <span style={{ fontWeight: 600 }}>0.000</span>
                                </div>
                            </div>
                            <div style={{ padding: "4px 8px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", marginBottom: "3px" }}>
                                    <span style={{ color: "#555" }}>Delivery Date</span>
                                    <span style={{ fontWeight: 600 }}>{delDate}</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                                    <span style={{ color: "#555" }}>Currency</span>
                                    <span style={{ fontWeight: 600 }}>{so.localCurrency || "PHP"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Items Table */}
                        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "4px", fontSize: "10px" }}>
                            <thead>
                                <tr style={{ background: "#d0d8e8", borderBottom: "2px solid #aaa" }}>
                                    {["Item", "Description", "Quantity", "Unit Price", "Amount"].map(h => (
                                        <th key={h} style={{ padding: "4px 8px", textAlign: h === "Quantity" || h === "Unit Price" || h === "Amount" ? "right" : "left", fontWeight: 700, border: "1px solid #ccc" }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(so.items || []).length === 0 ? (
                                    <tr><td colSpan={5} style={{ padding: "12px 8px", textAlign: "center", color: "#999", border: "1px solid #ccc" }}>No items</td></tr>
                                ) : (so.items || []).map((item, i) => (
                                    <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                                        <td style={{ padding: "4px 8px", border: "1px solid #ddd", fontWeight: 600 }}>{item.itemNo || "—"}</td>
                                        <td style={{ padding: "4px 8px", border: "1px solid #ddd" }}>{item.itemDescription || "—"}</td>
                                        <td style={{ padding: "4px 8px", border: "1px solid #ddd", textAlign: "right" }}>{Number(item.quantity || 0).toFixed(3)}</td>
                                        <td style={{ padding: "4px 8px", border: "1px solid #ddd", textAlign: "right" }}>{fmtAmt(item.unitPrice)}</td>
                                        <td style={{ padding: "4px 8px", border: "1px solid #ddd", textAlign: "right" }}>{fmtAmt(item.amount)}</td>
                                    </tr>
                                ))}
                                {Array.from({ length: Math.max(0, 4 - (so.items || []).length) }).map((_, i) => (
                                    <tr key={`e${i}`}>
                                        {[...Array(5)].map((_, j) => <td key={j} style={{ padding: "8px", border: "1px solid #ddd" }}>&nbsp;</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Totals */}
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
                            <div style={{ minWidth: "220px" }}>
                                <hr style={{ borderColor: "#ccc", borderWidth: "1px 0 0 0", marginBottom: "4px" }} />
                                {[
                                    { label: "Subtotal", val: fmtAmt(subtotal) },
                                    { label: "Freight", val: "0.00" },
                                    { label: "Tax", val: fmtAmt(tax) },
                                ].map(({ label, val }) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", padding: "1px 0" }}>
                                        <span style={{ color: "#555" }}>{label}</span>
                                        <span>{val}</span>
                                    </div>
                                ))}
                                <hr style={{ borderColor: "#ccc", borderWidth: "1px 0 0 0", margin: "4px 0" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", fontWeight: 700 }}>
                                    <span>Total Amount</span>
                                    <span>PHP {fmtAmt(total)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Signature */}
                        <div style={{ display: "flex", gap: "60px", marginTop: "24px" }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ borderTop: "1px solid #000", paddingTop: "4px", fontSize: "10px", color: "#555" }}>Signature</div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ borderTop: "1px solid #000", paddingTop: "4px", fontSize: "10px", color: "#555" }}>Date</div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── MAIN PAGE (SO List + Delivery Tab) ──────────────────────────────────────
const SO_And_Delivery_LIST = () => {
    const [mainTab, setMainTab] = useState("so");
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterField, setFilterField] = useState("all");
    const [sortField, setSortField] = useState("id");
    const [sortDir, setSortDir] = useState("desc");
    const [currentPage, setCurrentPage] = useState(1);
    const [viewSO, setViewSO] = useState(null);
    const [deliveryForm, setDeliveryForm] = useState(null);
    const [pickSO, setPickSO] = useState(null); // ← lives here only
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportMonth, setExportMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    });
    const itemsPerPage = 10;

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const res = await fetch(SO_API);
            if (res.ok) setOrders(await res.json());
        } catch (err) { console.error(err); }
        setLoading(false);
    };
    useEffect(() => { fetchOrders(); }, []);

    const allDeliveries = orders.filter(o => o.status === "Open - Printed" || o.status === "Delivered" || o.deliveryDate);
    const activeList = mainTab === "so" ? orders : allDeliveries;

    const filtered = activeList.filter((o) => {
        const q = searchTerm.toLowerCase();
        if (!q) return true;
        if (filterField === "all") return (
            String(o.number ?? "").includes(q) ||
            o.customer?.toLowerCase().includes(q) ||
            o.name?.toLowerCase().includes(q) ||
            o.status?.toLowerCase().includes(q) ||
            o.salesEmployee?.toLowerCase().includes(q)
        );
        return String(o[filterField] ?? "").toLowerCase().includes(q);
    });

    const sorted = [...filtered].sort((a, b) => {
        let av = a[sortField] ?? "", bv = b[sortField] ?? "";
        if (typeof av === "string") av = av.toLowerCase();
        if (typeof bv === "string") bv = bv.toLowerCase();
        return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

    const totalPages = Math.ceil(sorted.length / itemsPerPage);
    const paginated = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortField(field); setSortDir("asc"); }
        setCurrentPage(1);
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ChevronUp style={{ opacity: .3 }} size={14} />;
        return sortDir === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    const handleExportExcel = () => {
        const rows = sorted.map(o => ({
            "SO Number": o.number, "Status": o.status, "Customer Code": o.customer,
            "Customer Name": o.name, "Sales Employee": o.salesEmployee,
            "Posting Date": o.postingDate, "Delivery Date": o.deliveryDate,
            "Total": o.total,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, mainTab === "so" ? "Sales Orders" : "Deliveries");
        XLSX.writeFile(wb, `${mainTab === "so" ? "SO" : "Delivery"}_List_${Date.now()}.xlsx`);
    };

    const ViewModal = ({ so, onClose }) => {
        if (!so) return null;
        const subtotal = (so.items || []).reduce((s, i) => s + (Number(i.unitPrice) || 0) * (Number(i.quantity) || 0), 0);
        return (
            <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.55)", backdropFilter: "blur(6px)" }} onClick={onClose} />
                <div style={{ position: "relative", background: "#fff", borderRadius: "18px", boxShadow: "0 20px 60px rgba(0,0,0,.2)", width: "90%", maxWidth: "860px", maxHeight: "90vh", overflowY: "auto", zIndex: 10, fontFamily: '"Outfit",sans-serif' }}>
                    <div style={{ background: "linear-gradient(135deg,#3b82f6,#2563eb)", padding: "1.25rem 1.75rem", borderRadius: "18px 18px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                            <Eye size={20} color="#fff" />
                            <span style={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>Sales Order Detail</span>
                            <span style={{ background: "rgba(255,255,255,.2)", color: "#fff", fontSize: ".78rem", fontWeight: 600, padding: ".2rem .75rem", borderRadius: "20px" }}>SO #{so.number}</span>
                        </div>
                        <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: "8px", padding: ".4rem", cursor: "pointer", display: "flex", color: "#fff" }}><X size={18} /></button>
                    </div>
                    <div style={{ padding: "1.75rem" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginBottom: "1.5rem" }}>
                            <div style={{ background: "#f8faff", borderRadius: "12px", padding: "1.25rem", border: "1px solid #e2e8f0" }}>
                                <p style={{ fontSize: ".72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: ".875rem" }}>Customer Info</p>
                                {[{ label: "Customer Code", val: so.customer }, { label: "Customer Name", val: so.name }, { label: "Contact Person", val: so.contactPerson }, { label: "Customer Ref.", val: so.customerRefNo }, { label: "Local Currency", val: so.localCurrency }].map(({ label, val }) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: "1rem", marginBottom: ".5rem", fontSize: ".875rem" }}>
                                        <span style={{ color: "#64748b", fontWeight: 500 }}>{label}</span>
                                        <span style={{ color: "#1e293b", fontWeight: 600, textAlign: "right" }}>{val || "—"}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ background: "#f8faff", borderRadius: "12px", padding: "1.25rem", border: "1px solid #e2e8f0" }}>
                                <p style={{ fontSize: ".72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: ".875rem" }}>Order Info</p>
                                {[{ label: "Status", val: <StatusBadge status={so.status} /> }, { label: "Posting Date", val: fmtDate(so.postingDate) }, { label: "Delivery Date", val: fmtDate(so.deliveryDate) }, { label: "Sales Employee", val: so.salesEmployee }, { label: "Owner", val: so.owner }].map(({ label, val }) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: "1rem", marginBottom: ".5rem", fontSize: ".875rem" }}>
                                        <span style={{ color: "#64748b", fontWeight: 500 }}>{label}</span>
                                        <span style={{ color: "#1e293b", fontWeight: 600, textAlign: "right" }}>{val || "—"}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden", marginBottom: "1.25rem" }}>
                            <div style={{ background: "#3b82f6", padding: ".75rem 1.25rem" }}><p style={{ color: "#fff", fontWeight: 700, fontSize: ".85rem" }}>Items ({(so.items || []).length})</p></div>
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".83rem" }}>
                                    <thead><tr style={{ background: "#f8f9fa", borderBottom: "1px solid #e2e8f0" }}>
                                        {["#", "Item No.", "Description", "Qty", "UOM", "Whse", "Unit Price", "Amount"].map(h => <th key={h} style={{ padding: ".65rem 1rem", textAlign: "left", fontWeight: 700, color: "#475569", fontSize: ".75rem", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>)}
                                    </tr></thead>
                                    <tbody>
                                        {(so.items || []).length === 0 ? (
                                            <tr><td colSpan={8} style={{ padding: "2rem", textAlign: "center", color: "#94a3b8" }}>No items</td></tr>
                                        ) : (so.items || []).map((item, i) => (
                                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                                <td style={{ padding: ".65rem 1rem", color: "#94a3b8" }}>{i + 1}</td>
                                                <td style={{ padding: ".65rem 1rem", fontFamily: "monospace", fontWeight: 600, color: "#3b82f6" }}>{item.itemNo || "—"}</td>
                                                <td style={{ padding: ".65rem 1rem", color: "#334155" }}>{item.itemDescription || "—"}</td>
                                                <td style={{ padding: ".65rem 1rem", fontWeight: 600 }}>{item.quantity || 0}</td>
                                                <td style={{ padding: ".65rem 1rem", color: "#64748b" }}>{item.uom || "—"}</td>
                                                <td style={{ padding: ".65rem 1rem", color: "#64748b" }}>{item.whse || "—"}</td>
                                                <td style={{ padding: ".65rem 1rem", textAlign: "right" }}>{fmtAmt(item.unitPrice)}</td>
                                                <td style={{ padding: ".65rem 1rem", textAlign: "right", fontWeight: 600 }}>{fmtAmt(item.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <div style={{ background: "#f8faff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "1rem 1.5rem", minWidth: "280px" }}>
                                {[{ label: "Subtotal", val: fmtAmt(subtotal) }, { label: "Discount", val: fmtAmt(so.discount) }, { label: "Tax", val: fmtAmt(so.tax) }].map(({ label, val }) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: ".875rem", marginBottom: ".4rem" }}>
                                        <span style={{ color: "#64748b" }}>{label}</span><span style={{ color: "#334155" }}>{val}</span>
                                    </div>
                                ))}
                                <div style={{ borderTop: "2px solid #e2e8f0", marginTop: ".6rem", paddingTop: ".6rem", display: "flex", justifyContent: "space-between" }}>
                                    <span style={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b" }}>Total</span>
                                    <span style={{ fontWeight: 700, fontSize: "1rem", color: "#3b82f6" }}>PHP {fmtAmt(so.total)}</span>
                                </div>
                            </div>
                        </div>
                        {so.remarks && (
                            <div style={{ marginTop: "1.25rem", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "10px", padding: "1rem 1.25rem" }}>
                                <p style={{ fontSize: ".78rem", fontWeight: 700, color: "#92400e", marginBottom: ".35rem", textTransform: "uppercase" }}>Remarks</p>
                                <p style={{ color: "#78350f", fontSize: ".875rem" }}>{so.remarks}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {viewSO && <ViewModal so={viewSO} onClose={() => setViewSO(null)} />}
            {deliveryForm !== null && (
                <DeliveryForm
                    initialData={deliveryForm}
                    onClose={() => setDeliveryForm(null)}
                    onSaved={() => { fetchOrders(); setDeliveryForm(null); }}
                />
            )}
            {pickSO && <PickListModal so={pickSO} onClose={() => setPickSO(null)} />}

            <div style={{ minHeight: "100vh", background: "#f8f9fa", padding: "2.5rem 1.5rem", fontFamily: '"Outfit",-apple-system,sans-serif' }}>
                <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
          .glass { background:#fff; border:1px solid #e2e8f0; border-radius:16px; box-shadow:0 1px 4px rgba(0,0,0,.07); }
          .so-btn { padding:.65rem 1.25rem; border-radius:10px; border:none; font-weight:600; font-size:.85rem; cursor:pointer; transition:all .25s; display:inline-flex; align-items:center; gap:.5rem; font-family:"Outfit",sans-serif; }
          .so-btn-secondary { background:#fff; color:#475569; border:1px solid #e2e8f0; }
          .so-btn-secondary:hover { background:#f8f9fa; transform:translateY(-1px); }
          .so-btn-success { background:linear-gradient(135deg,#10b981,#059669); color:#fff; box-shadow:0 2px 8px rgba(16,185,129,.3); }
          .so-btn-success:hover { transform:translateY(-1px); }
          .so-btn-success:disabled { opacity:.5; cursor:not-allowed; transform:none; }
          .so-btn-delivery { background:linear-gradient(135deg,#f59e0b,#d97706); color:#fff; box-shadow:0 2px 8px rgba(245,158,11,.3); }
          .so-btn-delivery:hover { transform:translateY(-1px); }
          .so-input { width:100%; padding:.75rem 1rem; background:#fff; border:1px solid #e2e8f0; border-radius:10px; color:#1e293b; font-size:.9rem; font-family:"Outfit",sans-serif; }
          .so-input:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.1); }
          .so-input::placeholder { color:#94a3b8; }
          .so-table { width:100%; border-collapse:collapse; }
          .so-table th { background:#f8f9fa; padding:.9rem 1rem; text-align:left; font-weight:600; color:#475569; font-size:.78rem; text-transform:uppercase; letter-spacing:.05em; cursor:pointer; user-select:none; border-bottom:2px solid #e2e8f0; white-space:nowrap; }
          .so-table th:hover { background:#f1f5f9; }
          .so-table td { padding:.9rem 1rem; border-bottom:1px solid #f1f5f9; color:#334155; font-size:.875rem; vertical-align:middle; }
          .so-table tr:last-child td { border-bottom:none; }
          .so-table tr:hover td { background:#f8faff; }
          .so-select { padding:.65rem 2.5rem .65rem 1rem; background:#fff; border:1px solid #e2e8f0; border-radius:8px; color:#1e293b; font-size:.88rem; cursor:pointer; font-family:"Outfit",sans-serif; appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23475569' d='M6 9L1 4h10z'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right .875rem center; }
          .so-select:focus { outline:none; border-color:#3b82f6; }
          .so-badge { display:inline-block; padding:.3rem .75rem; background:#eff6ff; color:#3b82f6; border-radius:6px; font-size:.8rem; font-weight:600; }
          .so-page-btn { padding:.45rem .9rem; background:#fff; border:1px solid #e2e8f0; border-radius:8px; color:#475569; cursor:pointer; transition:all .2s; font-size:.85rem; font-family:"Outfit",sans-serif; min-width:40px; text-align:center; }
          .so-page-btn:hover:not(:disabled) { background:#f8f9fa; border-color:#3b82f6; color:#3b82f6; }
          .so-page-btn.active { background:linear-gradient(135deg,#3b82f6,#2563eb); border-color:#3b82f6; color:#fff; }
          .so-page-btn:disabled { opacity:.4; cursor:not-allowed; }
          .so-icon-btn { padding:.4rem .7rem; background:transparent; border:1px solid transparent; cursor:pointer; border-radius:8px; transition:all .2s; display:inline-flex; align-items:center; gap:.3rem; font-size:.78rem; font-weight:600; font-family:"Outfit",sans-serif; color:#64748b; }
          .so-icon-btn.view:hover { background:#eff6ff; border-color:#bfdbfe; color:#3b82f6; }
          .so-icon-btn.delivery:hover { background:#fef3c7; border-color:#fde68a; color:#d97706; }
          .so-icon-btn.gatepass:hover { background:#d1fae5; border-color:#6ee7b7; color:#059669; }
          .so-icon-btn.picklist:hover { background:#d1fae5; border-color:#6ee7b7; color:#059669; }
          .main-tab { padding:.75rem 1.75rem; border:none; font-weight:600; font-size:.9rem; cursor:pointer; transition:all .2s; font-family:"Outfit",sans-serif; border-bottom:3px solid transparent; background:transparent; }
          .main-tab.active { border-bottom-color:#3b82f6; color:#3b82f6; background:#eff6ff; }
          .main-tab:not(.active) { color:#64748b; }
          .main-tab:not(.active):hover { color:#334155; background:#f8f9fa; }
          .spinner { border:3px solid #e2e8f0; border-top:3px solid #3b82f6; border-radius:50%; width:24px; height:24px; animation:spin .8s linear infinite; }
          @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        `}</style>

                <div style={{ maxWidth: "1700px", margin: "0 auto" }}>

                    {/* Page Header */}
                    <div style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "1rem" }}>
                        <div style={{ padding: ".75rem", background: "linear-gradient(135deg,#3b82f6,#2563eb)", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(59,130,246,.3)" }}>
                            <FileText size={28} color="#fff" />
                        </div>
                        <div>
                            <h1 style={{ fontSize: "2.1rem", fontWeight: 700, background: "linear-gradient(135deg,#3b82f6,#2563eb)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: "-0.02em" }}>
                                {mainTab === "so" ? "Sales Order List" : "Delivery List"}
                            </h1>
                            <p style={{ color: "#64748b", fontSize: ".95rem" }}>Marketing — {mainTab === "so" ? "all sales orders" : "all deliveries"}</p>
                        </div>
                    </div>

                    {/* Main Tabs */}
                    <div className="glass" style={{ marginBottom: "1.25rem", overflow: "hidden" }}>
                        <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0" }}>
                            <button className={`main-tab ${mainTab === "so" ? "active" : ""}`} onClick={() => { setMainTab("so"); setCurrentPage(1); setSearchTerm(""); }}>
                                <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                                    <FileText size={16} /> Sales Orders
                                    <span style={{ background: "#e0f2fe", color: "#0369a1", padding: ".1rem .5rem", borderRadius: "10px", fontSize: ".75rem", fontWeight: 700 }}>{orders.length}</span>
                                </div>
                            </button>
                            <button className={`main-tab ${mainTab === "delivery" ? "active" : ""}`} onClick={() => { setMainTab("delivery"); setCurrentPage(1); setSearchTerm(""); }}>
                                <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                                    <Truck size={16} /> Deliveries
                                    <span style={{ background: "#fef3c7", color: "#92400e", padding: ".1rem .5rem", borderRadius: "10px", fontSize: ".75rem", fontWeight: 700 }}>{allDeliveries.length}</span>
                                </div>
                            </button>
                        </div>

                        {/* Controls */}
                        <div style={{ padding: "1.25rem 1.5rem" }}>
                            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center", marginBottom: "1rem" }}>
                                <div style={{ position: "relative", flex: "1 1 220px" }}>
                                    <Search size={15} style={{ position: "absolute", left: "1rem", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }} />
                                    <input type="text" placeholder={`Search ${mainTab === "so" ? "orders" : "deliveries"}...`} value={searchTerm}
                                        onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                        className="so-input" style={{ paddingLeft: "2.5rem" }} />
                                </div>
                                <div style={{ position: "relative" }}>
                                    <Filter size={14} style={{ position: "absolute", left: ".85rem", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }} />
                                    <select className="so-select" value={filterField} onChange={e => { setFilterField(e.target.value); setCurrentPage(1); }} style={{ paddingLeft: "2.3rem" }}>
                                        <option value="all">All Fields</option>
                                        <option value="number">SO Number</option>
                                        <option value="customer">Customer Code</option>
                                        <option value="name">Customer Name</option>
                                        <option value="status">Status</option>
                                        <option value="salesEmployee">Sales Employee</option>
                                    </select>
                                </div>
                                <button onClick={fetchOrders} className="so-btn so-btn-secondary" disabled={loading}>
                                    <RefreshCw size={14} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} /> Refresh
                                </button>
                                <div style={{ marginLeft: "auto", display: "flex", gap: ".5rem" }}>
                                    {mainTab === "delivery" && (
                                        <button onClick={() => setDeliveryForm({})} className="so-btn so-btn-delivery">
                                            <Plus size={14} /> New Delivery
                                        </button>
                                    )}
                                    <button onClick={() => setShowExportModal(true)} className="so-btn so-btn-success" disabled={sorted.length === 0}>
                                        <Download size={14} /> Export Excel
                                    </button>
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: ".75rem", paddingTop: ".875rem", borderTop: "1px solid #f1f5f9", flexWrap: "wrap" }}>
                                <span className="so-badge">Total: {activeList.length}</span>
                                <span className="so-badge">Filtered: {filtered.length}</span>
                                <span className="so-badge">Page {currentPage} of {totalPages || 1}</span>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="glass" style={{ marginBottom: "1.25rem", overflow: "hidden" }}>
                        <div style={{ overflowX: "auto" }}>
                            {loading ? (
                                <div style={{ padding: "4rem", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
                                    <div className="spinner" /><p style={{ color: "#64748b" }}>Loading...</p>
                                </div>
                            ) : paginated.length === 0 ? (
                                <div style={{ padding: "4rem", textAlign: "center" }}>
                                    <FileText size={42} color="#cbd5e1" style={{ margin: "0 auto 1rem" }} />
                                    <p style={{ color: "#64748b" }}>{searchTerm ? "No records match your search." : `No ${mainTab === "so" ? "sales orders" : "deliveries"} found.`}</p>
                                </div>
                            ) : (
                                <table className="so-table">
                                    <thead>
                                        <tr>
                                            {[
                                                { label: "SO #", field: "number" },
                                                { label: "Status", field: "status" },
                                                { label: "Customer Code", field: "customer" },
                                                { label: "Customer Name", field: "name" },
                                                { label: "Sales Employee", field: "salesEmployee" },
                                                { label: "Posting Date", field: "postingDate" },
                                                { label: "Delivery Date", field: "deliveryDate" },
                                                { label: "Total", field: "total" },
                                            ].map(col => (
                                                <th key={col.field} onClick={() => handleSort(col.field)}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: ".3rem" }}>{col.label} <SortIcon field={col.field} /></div>
                                                </th>
                                            ))}
                                            <th style={{ textAlign: "center", cursor: "default" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginated.map(o => (
                                            <tr key={o.id}>
                                                <td><span style={{ fontWeight: 700, color: "#3b82f6" }}>#{o.number}</span></td>
                                                <td><StatusBadge status={o.status} /></td>
                                                <td><span style={{ fontFamily: "monospace", fontWeight: 600, color: "#1e293b", fontSize: ".82rem" }}>{o.customer || "—"}</span></td>
                                                <td><span style={{ display: "block", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name || "—"}</span></td>
                                                <td>{o.salesEmployee || "—"}</td>
                                                <td style={{ whiteSpace: "nowrap", color: "#64748b" }}>{fmtDate(o.postingDate)}</td>
                                                <td style={{ whiteSpace: "nowrap", color: o.deliveryDate ? "#059669" : "#94a3b8", fontWeight: o.deliveryDate ? 600 : 400 }}>{fmtDate(o.deliveryDate)}</td>
                                                <td><span style={{ fontWeight: 700 }}>{fmtAmt(o.total)}</span></td>
                                                <td>
                                                    <div style={{ display: "flex", gap: ".35rem", justifyContent: "center", alignItems: "center" }}>
                                                        <button className="so-icon-btn view" onClick={() => setViewSO(o)}>
                                                            <Eye size={14} /> View
                                                        </button>
                                                        {mainTab === "so" ? (
                                                            <>
                                                                <button className="so-icon-btn picklist" onClick={() => setPickSO(o)}>
                                                                    <FileText size={14} /> Pick List
                                                                </button>
                                                                <button className="so-icon-btn delivery" onClick={() => setDeliveryForm({ ...o, id: o.id, preparedBy: getUser(), remarks: `Based On Sales Orders ${o.number}.` })}>
                                                                    <Truck size={14} /> Copy To Delivery
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button className="so-icon-btn delivery" onClick={() => setDeliveryForm({ ...o, preparedBy: o.preparedBy || getUser() })}>
                                                                    <FileText size={14} /> Edit
                                                                </button>
                                                                {o.deliveryDate && (
                                                                    <button className="so-icon-btn gatepass" onClick={() => setDeliveryForm({ ...o, _openGatepass: true, preparedBy: o.preparedBy || getUser() })}>
                                                                        <Download size={14} /> Gatepass
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="glass" style={{ padding: "1rem" }}>
                            <div style={{ display: "flex", gap: ".5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
                                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="so-page-btn">First</button>
                                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="so-page-btn">Prev</button>
                                {[...Array(totalPages)].map((_, i) => {
                                    const p = i + 1;
                                    if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1))
                                        return <button key={p} onClick={() => setCurrentPage(p)} className={`so-page-btn ${currentPage === p ? "active" : ""}`}>{p}</button>;
                                    if (p === currentPage - 2 || p === currentPage + 2) return <span key={p} style={{ color: "#94a3b8" }}>...</span>;
                                    return null;
                                })}
                                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="so-page-btn">Next</button>
                                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="so-page-btn">Last</button>
                            </div>
                        </div>
                    )}
                    {showExportModal && (
    <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.55)", backdropFilter: "blur(6px)" }} onClick={() => setShowExportModal(false)} />
        <div style={{
            position: "relative", background: "#fff", borderRadius: "18px",
            boxShadow: "0 20px 60px rgba(0,0,0,.25)", width: "90%", maxWidth: "780px",
            maxHeight: "90vh", display: "flex", flexDirection: "column", zIndex: 10,
            fontFamily: '"Outfit",sans-serif', overflow: "hidden"
        }}>
            {/* Header */}
            <div style={{ background: "linear-gradient(135deg,#1e3a8a,#2563eb)", padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                    <Download size={18} color="#fff" />
                    <span style={{ color: "#fff", fontWeight: 700, fontSize: "1rem" }}>Monthly Delivery Report</span>
                </div>
                <button onClick={() => setShowExportModal(false)} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: "8px", padding: ".4rem", cursor: "pointer", color: "#fff", display: "flex" }}>
                    <X size={18} />
                </button>
            </div>

            {/* Month Picker */}
            <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: "1rem" }}>
                <label style={{ fontSize: ".82rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>Select Month</label>
                <input
                    type="month"
                    value={exportMonth}
                    onChange={e => setExportMonth(e.target.value)}
                    style={{ padding: ".5rem .875rem", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: ".875rem", outline: "none", fontFamily: '"Outfit",sans-serif' }}
                />
                <span style={{ fontSize: ".82rem", color: "#94a3b8" }}>
                    {(() => {
                        const [y, m] = exportMonth.split("-");
                        const label = new Date(y, m - 1).toLocaleDateString("en-PH", { month: "long", year: "numeric" });
                        const deliveries = allDeliveries.filter(o => {
                            const d = o.deliveryDate || o.postingDate;
                            return d && d.startsWith(exportMonth);
                        });
                        const totalItems = deliveries.reduce((s, o) => s + (o.items || []).length, 0);
                        return `${label} — ${deliveries.length} deliveries, ${totalItems} line items`;
                    })()}
                </span>
            </div>

            {/* Table Preview */}
            <div style={{ overflowY: "auto", flex: 1 }}>
                {(() => {
                    const deliveries = allDeliveries.filter(o => {
                        const d = o.deliveryDate || o.postingDate;
                        return d && d.startsWith(exportMonth);
                    });

                    // Group by itemNo
                    const grouped = {};
                    deliveries.forEach(o => {
                        (o.items || []).forEach(item => {
                            if (!item.itemNo) return;
                            const key = item.itemNo;
                            if (!grouped[key]) {
                                grouped[key] = {
                                    itemCode: item.itemNo,
                                    itemDescription: item.itemDescription || "—",
                                    totalQty: 0,
                                };
                            }
                            grouped[key].totalQty += Number(item.quantity) || 0;
                        });
                    });

                    const rows = Object.values(grouped).sort((a, b) => b.totalQty - a.totalQty);

                    if (rows.length === 0) return (
                        <div style={{ padding: "3rem", textAlign: "center", color: "#94a3b8" }}>
                            <FileText size={36} style={{ margin: "0 auto 1rem", display: "block", color: "#cbd5e1" }} />
                            <p style={{ fontWeight: 600 }}>No deliveries found for this month.</p>
                        </div>
                    );

                    return (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
                            <thead>
                                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0", position: "sticky", top: 0 }}>
                                    {["#", "Item Code", "Item Description", "Total Qty Out"].map(h => (
                                        <th key={h} style={{ padding: ".75rem 1rem", textAlign: h === "Total Qty Out" ? "right" : "left", fontWeight: 700, color: "#64748b", fontSize: ".75rem", textTransform: "uppercase", letterSpacing: ".05em" }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, i) => (
                                    <tr key={row.itemCode} style={{ borderBottom: "1px solid #f1f5f9" }}
                                        onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                                        onMouseLeave={e => e.currentTarget.style.background = ""}>
                                        <td style={{ padding: ".65rem 1rem", color: "#94a3b8", fontSize: ".78rem" }}>{i + 1}</td>
                                        <td style={{ padding: ".65rem 1rem" }}>
                                            <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#1d4ed8", background: "#eff6ff", padding: ".2rem .5rem", borderRadius: "5px", fontSize: ".8rem" }}>
                                                {row.itemCode}
                                            </span>
                                        </td>
                                        <td style={{ padding: ".65rem 1rem", color: "#334155", maxWidth: "300px" }}>{row.itemDescription}</td>
                                        <td style={{ padding: ".65rem 1rem", textAlign: "right" }}>
                                            <span style={{ fontWeight: 700, color: "#059669", background: "#d1fae5", padding: ".2rem .75rem", borderRadius: "6px", fontSize: ".85rem" }}>
                                                {Number(row.totalQty).toLocaleString(undefined, { maximumFractionDigits: 3 })}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
                                    <td colSpan={3} style={{ padding: ".75rem 1rem", fontWeight: 700, color: "#475569", fontSize: ".82rem" }}>
                                        TOTAL — {rows.length} item{rows.length !== 1 ? "s" : ""}
                                    </td>
                                    <td style={{ padding: ".75rem 1rem", textAlign: "right", fontWeight: 700, color: "#1d4ed8", fontSize: ".9rem" }}>
                                        {rows.reduce((s, r) => s + r.totalQty, 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    );
                })()}
            </div>

            {/* Footer */}
            <div style={{ padding: "1rem 1.5rem", borderTop: "1px solid #e2e8f0", display: "flex", gap: ".75rem", justifyContent: "flex-end" }}>
                <button onClick={() => setShowExportModal(false)}
                    style={{ padding: ".625rem 1.25rem", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", fontWeight: 600, fontSize: ".875rem", cursor: "pointer", color: "#475569", fontFamily: '"Outfit",sans-serif' }}>
                    Cancel
                </button>
                <button onClick={() => {
                    const [y, m] = exportMonth.split("-");
                    const monthLabel = new Date(y, m - 1).toLocaleDateString("en-PH", { month: "long", year: "numeric" });
                    const deliveries = allDeliveries.filter(o => {
                        const d = o.deliveryDate || o.postingDate;
                        return d && d.startsWith(exportMonth);
                    });
                    const grouped = {};
                    deliveries.forEach(o => {
                        (o.items || []).forEach(item => {
                            if (!item.itemNo) return;
                            const key = item.itemNo;
                            if (!grouped[key]) grouped[key] = { itemCode: item.itemNo, itemDescription: item.itemDescription || "—", totalQty: 0 };
                            grouped[key].totalQty += Number(item.quantity) || 0;
                        });
                    });
                    const rows = Object.values(grouped).sort((a, b) => b.totalQty - a.totalQty);
                    const wsData = [
                        [`Monthly Delivery Report — ${monthLabel}`],
                        [],
                        ["#", "Item Code", "Item Description", "Total Qty Out"],
                        ...rows.map((r, i) => [i + 1, r.itemCode, r.itemDescription, r.totalQty]),
                        [],
                        ["", "", "TOTAL", rows.reduce((s, r) => s + r.totalQty, 0)],
                    ];
                    const ws = XLSX.utils.aoa_to_sheet(wsData);
                    ws["!cols"] = [{ wch: 5 }, { wch: 15 }, { wch: 40 }, { wch: 15 }];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Monthly Report");
                    XLSX.writeFile(wb, `Delivery_Monthly_${exportMonth}.xlsx`);
                }}
                    style={{ display: "flex", alignItems: "center", gap: ".5rem", padding: ".625rem 1.5rem", background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: ".875rem", cursor: "pointer", fontFamily: '"Outfit",sans-serif', boxShadow: "0 2px 8px rgba(16,185,129,.3)" }}>
                    <Download size={15} /> Export to Excel
                </button>
            </div>
        </div>
    </div>
)}

                </div>
            </div>
            
        </>
    );
};

export default SO_And_Delivery_LIST;